import React from 'react';
import type { ComponentConfig, CustomField } from '@puckeditor/core';
import styled from 'styled-components';

import { mediaQueries } from 'src/theme/constants/media';

import { renderFieldGroups } from '../fields/field-groups';
import type { FieldGroupDefinition } from '../fields/field-groups';

import { createFlatSpacingFields, readBoxSpacing } from '../fields/spacing-fields';
import type { FlatSpacingProps } from '../fields/spacing-fields';
import {
  BackgroundField,
  backgroundStyle,
  BACKGROUND_AI_SCHEMA,
  BACKGROUND_IMAGE_FIELDS,
  BackgroundOverlay,
  DEFAULT_BACKGROUND,
  DEFAULT_BACKGROUND_IMAGE_PROPS,
  backgroundImageStyle,
  backgroundOverlayStyle,
} from '../fields';
import type { BackgroundImageProps, BackgroundValue } from '../fields';
import { BACKGROUND_POSITION_OPTIONS, IMAGE_ONLY_FIELD_NAMES } from '../fields/background-image';
import { BLOCK_SPACING, CONTAINER_MIN_HEIGHTS, CONTAINER_WIDTHS, lookup, spacingToCss } from '../fields/tokens';
import type { BoxSpacing } from '../fields/tokens';
import {
  DEFAULT_MEDIA_ASSET_PROPS,
  normalizeMediaAsset,
  resolveMediaAssetData,
  resolveMediaAssetFields,
  videoSourceMimeType,
} from '../fields/media-asset';
import type { MediaAssetProps } from '../fields/media-asset';
import { MEDIA_ASSET_FIELDS } from '../fields/widgets/media-asset-fields';
import { selectField } from '../fields/widgets/select-field';
import { radioField } from '../fields/widgets/radio-field';

import { DecorativeLoop } from './decorative-loop';
import { getHeroStarterChildren } from './hero-starter';
import type { SlotProps } from './slot';
import { normalizeVideoPoster } from './video-source';

const VERTICAL_ALIGNMENT = {
  top: 'flex-start',
  center: 'center',
  bottom: 'flex-end',
} as const;

const HORIZONTAL_ALIGNMENT = {
  left: { marginLeft: '0px', marginRight: 'auto', alignItems: 'flex-start' },
  center: { marginLeft: 'auto', marginRight: 'auto', alignItems: 'center' },
  right: { marginLeft: 'auto', marginRight: '0px', alignItems: 'flex-end' },
} as const;

const MIN_HEIGHT_OPTIONS = [
  { label: 'Auto', value: 'auto' },
  { label: 'Medium', value: 'medium' },
  { label: 'Tall', value: 'tall' },
  { label: 'Full screen', value: 'full' },
] as const;

const VERTICAL_ALIGN_OPTIONS = [
  { label: 'Top', value: 'top' },
  { label: 'Center', value: 'center' },
  { label: 'Bottom', value: 'bottom' },
] as const;

const HORIZONTAL_ALIGN_OPTIONS = [
  { label: 'Left', value: 'left' },
  { label: 'Center', value: 'center' },
  { label: 'Right', value: 'right' },
] as const;

const MAX_WIDTH_OPTIONS = [
  { label: 'Narrow', value: 'narrow' },
  { label: 'Normal', value: 'normal' },
  { label: 'Wide', value: 'wide' },
  { label: 'Full width', value: 'none' },
] as const;

const DEFAULT_PADDING: BoxSpacing = { top: '64px', right: '24px', bottom: '64px', left: '24px' };
const DEFAULT_HERO_BACKGROUND: BackgroundValue = { ...DEFAULT_BACKGROUND };

const HeroContainer = styled.div`
  position: relative;
  isolation: isolate;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-sizing: border-box;
  width: 100%;
  justify-content: var(--cms-hero-vertical-align, center);
  min-height: var(--cms-hero-min-height, ${CONTAINER_MIN_HEIGHTS.medium});

  @media ${mediaQueries.largePhone} {
    justify-content: var(--cms-hero-mobile-vertical-align, var(--cms-hero-vertical-align, center));
    min-height: var(--cms-hero-mobile-min-height, var(--cms-hero-min-height, ${CONTAINER_MIN_HEIGHTS.medium}));
  }
`;

/** Sits behind the overlay and the content; never changes the block's size. */
const HeroVideoLayer = styled.div`
  position: absolute;
  inset: 0;
  z-index: 0;
  overflow: hidden;
  pointer-events: none;
`;

/** Editor-only: the poster stands in for the video so authoring never autoplays. */
const EditorPoster = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: var(--cms-decorative-loop-position, center);
`;

const POSITION_VALUES: ReadonlySet<string> = new Set(BACKGROUND_POSITION_OPTIONS.map(({ value }) => value));

function backgroundPositionOrDefault(value: unknown): string {
  return typeof value === 'string' && POSITION_VALUES.has(value)
    ? value
    : DEFAULT_BACKGROUND_IMAGE_PROPS.backgroundPosition;
}

/** Image size and repeat apply to an image only; a video always covers. Hidden saved values are never cleared. */
function resolveHeroMediaFields<Fields extends Record<string, unknown>>(mediaAsset: unknown, fields: Fields): Fields {
  const { kind } = normalizeMediaAsset(mediaAsset);
  const scoped = resolveMediaAssetFields(mediaAsset, fields);
  let hidden: readonly string[] = [];
  if (kind === '') hidden = IMAGE_ONLY_FIELD_NAMES;
  else if (kind === 'video') hidden = ['backgroundSize', 'backgroundRepeat'];
  return Object.fromEntries(Object.entries(scoped).filter(([name]) => !hidden.includes(name))) as Fields;
}

export const HeroContent = styled.div`
  position: relative;
  z-index: 1;
  box-sizing: border-box;
  align-items: var(--cms-hero-horizontal-align, center);
  margin-left: var(--cms-hero-margin-left, auto);
  margin-right: var(--cms-hero-margin-right, auto);
  --cms-hero-content-justify: var(--cms-hero-horizontal-align, center);

  @media ${mediaQueries.largePhone} {
    align-items: var(--cms-hero-mobile-horizontal-align, var(--cms-hero-horizontal-align, center));
    margin-left: var(--cms-hero-mobile-margin-left, var(--cms-hero-margin-left, auto));
    margin-right: var(--cms-hero-mobile-margin-right, var(--cms-hero-margin-right, auto));
    --cms-hero-content-justify: var(--cms-hero-mobile-horizontal-align, var(--cms-hero-horizontal-align, center));
  }

  /*
   * Puck wraps editor slot children, while production renders them directly. Center alignment
   * shrink-wraps that editor-only wrapper, so a nested button row's full width would otherwise
   * shrink to its children and responsive layout would disagree with production. Container
   * children own a nested dropzone; stretch only those wrappers so direct Heading/Text/Button/
   * Image alignment keeps its existing behavior.
   */
  & > [data-puck-component]:has([data-puck-dropzone]) {
    width: 100%;
  }
`;

/** The background is one image-or-video asset (`mediaAsset`); a video also needs a `mediaPoster`. */
export type HeroProps = FlatSpacingProps<'padding'> &
  Omit<BackgroundImageProps, 'backgroundImage'> &
  Pick<MediaAssetProps, 'mediaAsset' | 'mediaPoster'> & {
    content?: React.FC<SlotProps>;
    background: BackgroundValue;
    minHeight: keyof typeof CONTAINER_MIN_HEIGHTS;
    verticalAlign: keyof typeof VERTICAL_ALIGNMENT;
    horizontalAlign: keyof typeof HORIZONTAL_ALIGNMENT;
    maxWidth: keyof typeof CONTAINER_WIDTHS;
    mobileMode?: 'customize' | 'desktop';
    mobileMinHeight?: keyof typeof CONTAINER_MIN_HEIGHTS;
    mobileVerticalAlign?: keyof typeof VERTICAL_ALIGNMENT;
    mobileHorizontalAlign?: keyof typeof HORIZONTAL_ALIGNMENT;
  };

const HERO_FIELD_GROUPS: readonly FieldGroupDefinition<keyof HeroProps>[] = [
  {
    label: 'Appearance',
    fields: [
      'mediaAsset',
      'mediaPoster',
      'backgroundSize',
      'backgroundPosition',
      'backgroundRepeat',
      'overlayOpacity',
      'background',
    ],
  },
  { label: 'Layout', fields: ['minHeight', 'verticalAlign', 'horizontalAlign', 'maxWidth'] },
  { label: 'Mobile', fields: ['mobileMode', 'mobileMinHeight', 'mobileVerticalAlign', 'mobileHorizontalAlign'] },
  {
    label: 'Spacing',
    fields: [
      { label: 'Padding', variant: 'card', fields: ['paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft'] },
    ],
  },
];

function resolveBackground(value: unknown): BackgroundValue {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return DEFAULT_HERO_BACKGROUND;
  const { color } = value as { color?: unknown };
  return { color: typeof color === 'string' ? color : DEFAULT_HERO_BACKGROUND.color };
}

function resolvePadding(value: unknown): BoxSpacing {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return DEFAULT_PADDING;
  const candidate = value as Partial<Record<keyof BoxSpacing, unknown>>;
  const side = (key: keyof BoxSpacing): string =>
    typeof candidate[key] === 'string' ? candidate[key] : DEFAULT_PADDING[key];

  return { top: side('top'), right: side('right'), bottom: side('bottom'), left: side('left') };
}

export const heroConfig: ComponentConfig<HeroProps> = {
  label: 'Hero',
  ai: {
    instructions:
      'A page-leading banner with a flexible content slot. Compose concise Heading, Text, Button and Image children directly. Use one h1 only when this is the page lead. Set text color on individual Heading and Text children.',
  },
  fields: {
    content: { type: 'slot', allow: ['Heading', 'Paragraph', 'Button', 'Image', 'ButtonRow'] },
    mediaAsset: { ...MEDIA_ASSET_FIELDS.mediaAsset, label: 'Background media' },
    mediaPoster: MEDIA_ASSET_FIELDS.mediaPoster,
    backgroundSize: BACKGROUND_IMAGE_FIELDS.backgroundSize,
    backgroundPosition: BACKGROUND_IMAGE_FIELDS.backgroundPosition,
    backgroundRepeat: BACKGROUND_IMAGE_FIELDS.backgroundRepeat,
    overlayOpacity: BACKGROUND_IMAGE_FIELDS.overlayOpacity,
    background: {
      type: 'custom',
      label: 'Background color',
      render: BackgroundField,
      ai: {
        schema: BACKGROUND_AI_SCHEMA,
        instructions:
          "Fallback color shown when there is no image or video or it fails to load. Prefer a theme color role; 'none' is transparent.",
      },
    },
    minHeight: selectField(
      'Height',
      MIN_HEIGHT_OPTIONS,
      "'Medium' (~320px) is the recommended default; 'Tall' (~480px) gives a more prominent banner."
    ),
    verticalAlign: {
      type: 'radio',
      label: 'Vertical alignment',
      options: [...VERTICAL_ALIGN_OPTIONS],
      ai: { schema: { type: 'string', enum: VERTICAL_ALIGN_OPTIONS.map(({ value }) => value) } },
    },
    horizontalAlign: {
      type: 'radio',
      label: 'Horizontal alignment',
      options: [...HORIZONTAL_ALIGN_OPTIONS],
      ai: { schema: { type: 'string', enum: HORIZONTAL_ALIGN_OPTIONS.map(({ value }) => value) } },
    },
    maxWidth: selectField(
      'Content width',
      MAX_WIDTH_OPTIONS,
      "Constrains the content column. 'Narrow' keeps hero copy readable; 'Full width' spans the banner."
    ),
    mobileMode: {
      type: 'radio',
      label: 'Mobile settings',
      options: [
        { label: 'Use desktop', value: 'desktop' },
        { label: 'Customize', value: 'customize' },
      ],
      ai: { schema: { type: 'string', enum: ['desktop', 'customize'] } },
    },
    mobileMinHeight: selectField(
      'Height',
      [MIN_HEIGHT_OPTIONS[1], MIN_HEIGHT_OPTIONS[0], MIN_HEIGHT_OPTIONS[2], MIN_HEIGHT_OPTIONS[3]],
      'Minimum height on phones. When unset, follows the desktop Height.'
    ) as CustomField<HeroProps['mobileMinHeight']>,
    mobileVerticalAlign: {
      type: 'radio',
      label: 'Content position',
      options: [VERTICAL_ALIGN_OPTIONS[1], VERTICAL_ALIGN_OPTIONS[0], VERTICAL_ALIGN_OPTIONS[2]],
      ai: { schema: { type: 'string', enum: VERTICAL_ALIGN_OPTIONS.map(({ value }) => value) } },
    },
    mobileHorizontalAlign: {
      type: 'radio',
      label: 'Content alignment',
      options: [HORIZONTAL_ALIGN_OPTIONS[1], HORIZONTAL_ALIGN_OPTIONS[0], HORIZONTAL_ALIGN_OPTIONS[2]],
      ai: { schema: { type: 'string', enum: HORIZONTAL_ALIGN_OPTIONS.map(({ value }) => value) } },
    },
    ...createFlatSpacingFields('padding'),
  },
  renderFields: ({ fields }) => renderFieldGroups(fields, HERO_FIELD_GROUPS),
  // The scrim only paints over a background image, so offer it only once one exists. The stored
  // value is untouched, so removing and re-adding an image restores the author's chosen strength.
  resolveFields: (data, { fields }) => {
    let resolvedFields = { ...fields };
    if (data.props.mobileMode !== 'customize') {
      const {
        mobileMinHeight: _mobileMinHeight,
        mobileVerticalAlign: _mobileVerticalAlign,
        mobileHorizontalAlign: _mobileHorizontalAlign,
        ...desktopFields
      } = resolvedFields;
      resolvedFields = desktopFields;
    } else {
      resolvedFields.mobileMinHeight = selectField(
        'Height',
        [MIN_HEIGHT_OPTIONS[1], MIN_HEIGHT_OPTIONS[0], MIN_HEIGHT_OPTIONS[2], MIN_HEIGHT_OPTIONS[3]],
        'Minimum height on phones. When unset, follows the desktop Height.',
        undefined,
        data.props.minHeight
      ) as CustomField<HeroProps['mobileMinHeight']>;
      resolvedFields.mobileVerticalAlign = radioField(
        'Content position',
        [VERTICAL_ALIGN_OPTIONS[1], VERTICAL_ALIGN_OPTIONS[0], VERTICAL_ALIGN_OPTIONS[2]],
        data.props.verticalAlign
      ) as CustomField<HeroProps['mobileVerticalAlign']>;
      resolvedFields.mobileHorizontalAlign = radioField(
        'Content alignment',
        [HORIZONTAL_ALIGN_OPTIONS[1], HORIZONTAL_ALIGN_OPTIONS[0], HORIZONTAL_ALIGN_OPTIONS[2]],
        data.props.horizontalAlign
      ) as CustomField<HeroProps['mobileHorizontalAlign']>;
    }

    return resolveHeroMediaFields(data.props.mediaAsset, resolvedFields as typeof fields);
  },
  resolveData: ({ props }) => ({ props: resolveMediaAssetData(props) }),
  defaultProps: {
    backgroundSize: DEFAULT_BACKGROUND_IMAGE_PROPS.backgroundSize,
    backgroundPosition: DEFAULT_BACKGROUND_IMAGE_PROPS.backgroundPosition,
    backgroundRepeat: DEFAULT_BACKGROUND_IMAGE_PROPS.backgroundRepeat,
    overlayOpacity: DEFAULT_BACKGROUND_IMAGE_PROPS.overlayOpacity,
    mediaAsset: DEFAULT_MEDIA_ASSET_PROPS.mediaAsset,
    mediaPoster: DEFAULT_MEDIA_ASSET_PROPS.mediaPoster,
    background: DEFAULT_HERO_BACKGROUND,
    minHeight: 'medium',
    verticalAlign: 'center',
    horizontalAlign: 'center',
    maxWidth: 'narrow',
    paddingTop: DEFAULT_PADDING.top,
    paddingRight: DEFAULT_PADDING.right,
    paddingBottom: DEFAULT_PADDING.bottom,
    paddingLeft: DEFAULT_PADDING.left,
    mobileMode: 'desktop',
    content: (getHeroStarterChildren() as unknown) as React.FC<SlotProps>,
  },
  render: ({
    content: slotContent,
    mediaAsset,
    mediaPoster,
    backgroundSize = DEFAULT_BACKGROUND_IMAGE_PROPS.backgroundSize,
    backgroundPosition = DEFAULT_BACKGROUND_IMAGE_PROPS.backgroundPosition,
    backgroundRepeat = DEFAULT_BACKGROUND_IMAGE_PROPS.backgroundRepeat,
    overlayOpacity = DEFAULT_BACKGROUND_IMAGE_PROPS.overlayOpacity,
    background = DEFAULT_HERO_BACKGROUND,
    minHeight = 'medium',
    verticalAlign = 'center',
    horizontalAlign = 'center',
    maxWidth = 'narrow',
    mobileMode,
    mobileMinHeight,
    mobileVerticalAlign,
    mobileHorizontalAlign,
    puck,
    ...spacingProps
  }) => {
    if (!slotContent) return <></>;
    const Slot = slotContent;
    const resolvedBackground = resolveBackground(background);
    const resolvedHorizontalAlign = lookup(HORIZONTAL_ALIGNMENT, horizontalAlign, HORIZONTAL_ALIGNMENT.center);
    // Ignore stored overrides in desktop mode without deleting them, so Customize restores them.
    const isCustomize = mobileMode === 'customize';
    const mobileHorizontal =
      isCustomize && typeof mobileHorizontalAlign === 'string'
        ? lookup<typeof HORIZONTAL_ALIGNMENT[keyof typeof HORIZONTAL_ALIGNMENT] | undefined>(
            HORIZONTAL_ALIGNMENT,
            mobileHorizontalAlign,
            undefined
          )
        : undefined;
    const mobileHeight = isCustomize ? mobileMinHeight : undefined;
    const containerStyle = {
      '--cms-hero-vertical-align': lookup(VERTICAL_ALIGNMENT, verticalAlign, VERTICAL_ALIGNMENT.center),
      '--cms-hero-mobile-vertical-align':
        isCustomize && typeof mobileVerticalAlign === 'string'
          ? lookup<string | undefined>(VERTICAL_ALIGNMENT, mobileVerticalAlign, undefined)
          : undefined,
      '--cms-hero-min-height': lookup(CONTAINER_MIN_HEIGHTS, minHeight, CONTAINER_MIN_HEIGHTS.medium),
      '--cms-hero-mobile-min-height':
        typeof mobileHeight === 'string'
          ? lookup<string | undefined>(CONTAINER_MIN_HEIGHTS, mobileHeight, undefined)
          : undefined,
    } as React.CSSProperties;
    // Production render may omit `puck`; the editor never autoplays, it shows the poster.
    const isEditing = Boolean((puck as Partial<typeof puck> | undefined)?.isEditing);
    // Hero's old `backgroundImage` prop is intentionally not read: no saved Hero with it is supported (no migration, by decision).
    const media = normalizeMediaAsset(mediaAsset);
    const poster = normalizeVideoPoster(mediaPoster);
    const posterSrc = poster.kind === 'none' ? '' : poster.src;
    const hasImage = media.kind === 'image';
    // A background video needs its poster: it is the first paint and all that phones and
    // reduced-motion visitors see. Without one the block falls back to its color.
    const hasVideo = media.kind === 'video' && posterSrc !== '';
    const backgroundImage = hasImage ? media.src : '';

    return (
      <HeroContainer
        data-testid='cms-hero'
        style={{
          ...backgroundStyle(resolvedBackground),
          ...backgroundImageStyle({ backgroundImage, backgroundSize, backgroundPosition, backgroundRepeat }),
          ...(hasVideo
            ? ({
                '--cms-decorative-loop-position': backgroundPositionOrDefault(backgroundPosition),
              } as React.CSSProperties)
            : {}),
          ...containerStyle,
          padding: spacingToCss(resolvePadding(readBoxSpacing(spacingProps, 'padding'))),
        }}
      >
        {hasVideo && (
          <HeroVideoLayer aria-hidden='true'>
            {isEditing ? (
              <EditorPoster src={posterSrc} alt='' data-testid='cms-hero-video-poster' />
            ) : (
              <DecorativeLoop
                src={media.src}
                mimeType={videoSourceMimeType(media.src, media.mimeType)}
                posterSrc={posterSrc}
                testId='cms-hero-video'
              />
            )}
          </HeroVideoLayer>
        )}
        {(hasImage || hasVideo) && (
          <BackgroundOverlay
            data-testid='cms-hero-overlay'
            aria-hidden='true'
            style={backgroundOverlayStyle(overlayOpacity)}
          />
        )}
        <Slot
          as={HeroContent}
          minEmptyHeight={120}
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: BLOCK_SPACING.sm,
            width: '100%',
            maxWidth: lookup(CONTAINER_WIDTHS, maxWidth, CONTAINER_WIDTHS.narrow),
            ...({
              '--cms-hero-horizontal-align': resolvedHorizontalAlign.alignItems,
              '--cms-hero-margin-left': resolvedHorizontalAlign.marginLeft,
              '--cms-hero-margin-right': resolvedHorizontalAlign.marginRight,
              '--cms-hero-mobile-horizontal-align': mobileHorizontal?.alignItems,
              '--cms-hero-mobile-margin-left': mobileHorizontal?.marginLeft,
              '--cms-hero-mobile-margin-right': mobileHorizontal?.marginRight,
            } as React.CSSProperties),
          }}
        />
      </HeroContainer>
    );
  },
};
